if (!customElements.get('td-quantity-deals')) {
  class TdQuantityDeals extends HTMLElement {
    static SELECTORS = {
      quantitySelector: 'quantity-selector-component',
      quantityInput: '[ref="quantityInput"]',
      dealButton: '[data-td-quantity-deal]',
      currentQuantity: '[data-td-quantity-current]',
    };

    constructor() {
      super();
      this.quantitySelector = null;
      this.dealButtons = [];
      this.currentQuantityNode = null;

      this.handleClick = this.handleClick.bind(this);
      this.handleQuantityUpdate = this.handleQuantityUpdate.bind(this);
    }

    connectedCallback() {
      this.quantitySelector = this.querySelector(TdQuantityDeals.SELECTORS.quantitySelector);
      this.dealButtons = Array.from(this.querySelectorAll(TdQuantityDeals.SELECTORS.dealButton));
      this.currentQuantityNode = this.querySelector(TdQuantityDeals.SELECTORS.currentQuantity);

      if (!(this.quantitySelector instanceof HTMLElement)) {
        return;
      }

      this.addEventListener('click', this.handleClick);
      this.addEventListener('quantity-selector:update', this.handleQuantityUpdate);
      const currentQuantity = this.getCurrentQuantity();
      this.updateSummary(currentQuantity);
      this.syncDealState(currentQuantity);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('quantity-selector:update', this.handleQuantityUpdate);
    }

    handleClick(event) {
      const trigger = event.target.closest(TdQuantityDeals.SELECTORS.dealButton);
      if (!(trigger instanceof HTMLElement) || !this.contains(trigger)) {
        return;
      }

      event.preventDefault();
      const targetQuantity = Number(trigger.dataset.tdQuantityValue);
      if (!Number.isFinite(targetQuantity) || targetQuantity <= 0) {
        return;
      }

      this.setQuantity(targetQuantity);
    }

    handleQuantityUpdate(event) {
      if (!(event.target instanceof Node) || !this.contains(event.target)) {
        return;
      }

      let nextQuantity = Number(event.detail?.quantity);
      if (!Number.isFinite(nextQuantity)) {
        nextQuantity = this.getCurrentQuantity();
      }

      if (!Number.isFinite(nextQuantity)) {
        return;
      }

      this.updateSummary(nextQuantity);
      this.syncDealState(nextQuantity);
    }

    getCurrentQuantity() {
      const input = this.quantitySelector?.querySelector(TdQuantityDeals.SELECTORS.quantityInput);
      if (!(input instanceof HTMLInputElement)) {
        return Number(this.dataset.tdInitialQuantity) || 1;
      }

      return Number(input.value) || Number(this.dataset.tdInitialQuantity) || 1;
    }

    setQuantity(rawQuantity) {
      if (!(this.quantitySelector instanceof HTMLElement)) {
        return;
      }

      const input = this.quantitySelector.querySelector(TdQuantityDeals.SELECTORS.quantityInput);
      if (!(input instanceof HTMLInputElement)) {
        return;
      }

      let quantity = rawQuantity;
      if (typeof this.quantitySelector.getCurrentValues === 'function') {
        const values = this.quantitySelector.getCurrentValues();
        const minQuantity = Number(values?.min) || 1;
        const stepQuantity = Number(values?.step) || 1;
        const maxQuantity =
          typeof this.quantitySelector.getEffectiveMax === 'function' ? this.quantitySelector.getEffectiveMax() : null;

        quantity = Math.max(minQuantity, quantity);
        if (maxQuantity !== null && Number.isFinite(Number(maxQuantity))) {
          quantity = Math.min(Number(maxQuantity), quantity);
        }

        if ((quantity - minQuantity) % stepQuantity !== 0) {
          quantity = minQuantity + Math.floor((quantity - minQuantity) / stepQuantity) * stepQuantity;
        }
      }

      input.value = String(quantity);

      if (typeof this.quantitySelector.onQuantityChange === 'function') {
        this.quantitySelector.onQuantityChange();
      } else {
        this.quantitySelector.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (typeof this.quantitySelector.updateButtonStates === 'function') {
        this.quantitySelector.updateButtonStates();
      }

      this.updateSummary(quantity);
      this.syncDealState(quantity);
    }

    syncDealState(currentQuantity) {
      if (this.dealButtons.length === 0) {
        return;
      }

      const eligibleDeals = this.dealButtons
        .map((button) => ({
          button,
          quantity: Number(button.dataset.tdQuantityValue),
        }))
        .filter((deal) => Number.isFinite(deal.quantity) && deal.quantity > 0)
        .filter((deal) => currentQuantity >= deal.quantity)
        .sort((a, b) => b.quantity - a.quantity);

      const activeButton = eligibleDeals[0]?.button ?? null;

      this.dealButtons.forEach((button) => {
        button.setAttribute('aria-pressed', button === activeButton ? 'true' : 'false');
      });
    }

    updateSummary(quantity) {
      if (!(this.currentQuantityNode instanceof HTMLElement)) {
        return;
      }

      this.currentQuantityNode.textContent = String(quantity);
    }
  }

  customElements.define('td-quantity-deals', TdQuantityDeals);
}
